/*
    Workspace represents the separate working space with one or groups raster areas, vector data, common description and properties
    (c) starshiptroopers.dev

 */

import {Evented} from "@/engine/evented.js";
import {mapApi, mapContext} from "@/context/global.js";
import {gcUtils} from "@/engine/helpers/gcUtils.js";
import {markerIcons} from "@/engine/marker/markerFactory.js";
import * as L from "leaflet";
import {AMUtil} from "@/engine/leaflet/antimeridianUtils.js";
import {VectorLayerDrawing} from "@/engine/layers/vectorLayerDrawing.js";
import {RasterLayer} from "@/engine/layers/rasterLayer.js";
import {Layer3D} from "@/engine/layers/layer3D.js";
import {VectorLayer} from "@/engine/layers/vectorLayer.js";
import {
    LAYER_TYPE_VECTOR_COMMON,
    LAYER_TYPE_VECTOR_ELEVATION,
    LAYER_TYPE_VECTOR_LINE,
    LAYER_TYPE_VECTOR_POINT,
    LAYER_TYPE_VECTOR_POLYGON
} from "@/engine/layers/layer.js";
import {VectorLayerTMS} from "@/engine/layers/vectorLayerTMS.js";
import {VectorLayerElevation} from "@/engine/layers/vectorLayerElevation.js";
import {elog} from "@/engine/helpers/logging.js";

export const LAYER_TYPE_RASTER = 1
export const LAYER_TYPE_VECTOR = 2
export const LAYER_TYPE_3D = 3

const WORKSPACE_MAX_ZOOM = 32
const WORKSPACE_MIN_SIZE_IN_PIXELS_TO_SHOW = 20; // is used on layers with vector data only

class Workspace extends Evented {
    constructor(struct) {
        super()
        this.defaultName            = "workspace" //default name
        this.id                     = struct.id
        this.name                   = this.defaultName
        this.visible                = typeof struct.visible !== 'undefined' ? struct.visible : true
        this.geoCodeHuman           = ""
        this.geodata                = struct.geodata
        this.mine                   = struct.mine
        this.writable               = struct.writable
        this.public                 = struct.public
        this.publicDownload         = struct.publicDownload
        this.shared                 = struct.shared
        this.preview                = struct.preview
        // this is the layer that covers the whole world
        this.global                 = struct.global
        this.debug                  = typeof struct.debug !== 'undefined' ? struct.debug : false
        /** @type {LatLngBounds} */
        this.bounds                 = null;
        this.vectorLayers           = new Map();
        this.rasterLayers           = new Map();
        this.map                    = null;
        this._shareLinks            = null;
        this.maxZoom                = WORKSPACE_MAX_ZOOM; // the value overloaded from rasterLayers
        this.minZoom                = 0;  // the value overloaded from rasterLayers
        this.marker                 = null;
        this.markerDefMaxZoom       = 13;
        this.showMarker             = typeof struct.showMarker !== 'undefined' ? struct.showMarker : true;
        this.hiddenByZoomlevel      = false;
        this._layersCoveredBounds    = null;
        this._layersNumber          = 0;
        this._firstRasterLayer      = null
        this._firstVectorLayer      = null

        this.initBounds(struct.bounds)

        //logging function
        this.elog = (typeof elog === "function") ? elog : function (severity, message) {
            if (typeof console[severity.toLowerCase()] === "function") {
                console[severity.toLowerCase()](message);
            }
        };

        if (this.geodata) {
            this.geoCodeHuman = this._geoCodeHuman()
        }

        if (struct.layers) {
            for (let layer of struct.layers) {
                this.addLayer(layer)
            }
        }
        this.updateProperties(struct);
        this.updatePropertiesHandler = mapContext.RPCUpdateProperties;
    }

    addLayer(layerStruct) {
        let l = this.getLayer(layerStruct.id)
        if (l)
            return l
        // it's simple logic of defining the layer order
        // it's better to do it more smart
        // and there is an issue with correct layer order for new layers when some intermediate layer was removed
        layerStruct.order = this._layersNumber

        layerStruct = Object.assign({}, layerStruct,
            {
                // inherit visible from workspace if it's not defined
                ...(layerStruct.visible !== undefined ? {} : { visible: this.visible }),
                workspace: this,
                debug: this.debug,
            })

        if (layerStruct.type === LAYER_TYPE_RASTER || layerStruct.type === LAYER_TYPE_3D) {
            l = this._addRasterLayer(layerStruct)
            if (! this._firstRasterLayer) {
                this._firstRasterLayer = l
            }
        } else if (layerStruct.type === LAYER_TYPE_VECTOR) {
            l = this._addVectorLayer(layerStruct)
            if (! (l instanceof VectorLayerDrawing) && ! this._firstVectorLayer) {
                this._firstVectorLayer = l
            }
        }
        if (l && this.map) {
            l.addOnMap(this.map)
        }
        this._layersNumber++
        this.updateMarker(null, true)
        this._layersCoveredBounds = null
        this.layersCoveredBounds()

        mapContext.fire("layer:add", {layer: l});
        return l
    }

    removeLayer(l) {
        if (l instanceof RasterLayer) {
            this.rasterLayers.delete(l.id)
            if (this._firstRasterLayer === l) {
                this._firstRasterLayer = null
                for (let [k, v] of this.rasterLayers) {
                    this._firstRasterLayer = v
                    break
                }
            }
        }
        if (l instanceof VectorLayer) {
            this.vectorLayers.delete(l.id)
            if (this._firstVectorLayer === l) {
                this._firstVectorLayer = null
                for (let [k, v] of this.vectorLayers) {
                    if (! (v instanceof VectorLayerDrawing)) {
                        this._firstVectorLayer = v
                        break
                    }
                }
            }
        }
        this._layersNumber--
        l.removeFromMap()

        this._layersCoveredBounds = null
        this.layersCoveredBounds()

        mapContext.fire("layer:remove", {layer: l});
    }

    recreateLayer(layerStruct) {
        let removingLayer = this.getLayer(layerStruct.id)
        if (!removingLayer)
            return
        this.removeLayer(removingLayer)
        let l = this.addLayer(layerStruct)
        mapContext.fire("layer:recreate", {layer: l});
        return l
    }

    _addVectorLayer(layerStruct) {
        let layer = null

        if (layerStruct.dataType === LAYER_TYPE_VECTOR_LINE ||
            layerStruct.dataType === LAYER_TYPE_VECTOR_POINT ||
            layerStruct.dataType === LAYER_TYPE_VECTOR_POLYGON) {
            layer = new VectorLayerDrawing(layerStruct)
            layer.on("boundschanged", function () {
                this._layersCoveredBounds = null
                this.layersCoveredBounds()
            }.bind(this))
        } else {
            if (layerStruct.dataType === LAYER_TYPE_VECTOR_ELEVATION) {
                layer = new VectorLayerElevation(layerStruct)
            } else if (layerStruct.dataType === LAYER_TYPE_VECTOR_COMMON) {
                layer = new VectorLayerTMS(layerStruct)
            } else {
                this.elog("ERROR", `Unsupported type '${layerStruct.dataType}' of the layer ${layerStruct.id}`)
            }
            this._extendBoundsFromLayer(layer)
        }

        if (! layer) {
            return
        }
        this.vectorLayers.set(layer.id, layer)

        // wrong srs
        if (layer.isWrongSrs()) {
            mapContext.fire("layer:wrongSRS", {layer: layer});
        }

        return layer
    }

    _addRasterLayer(layerStruct) {

        let layer = layerStruct.type === LAYER_TYPE_3D ? new Layer3D(layerStruct) : new RasterLayer(layerStruct);

        this._extendBoundsFromLayer(layer)

        if (layer.geometry) {
            this.minZoom = this.minZoom === 0 ? layer.geometry.minZoom: Math.min(layer.geometry.minZoom, this.minZoom);
            this.maxZoom = this.maxZoom === WORKSPACE_MAX_ZOOM ? layer.geometry.maxZoom : Math.max(layer.geometry.maxZoom, this.maxZoom);
        } else if (layer instanceof Layer3D) {
            // 3D layers can be viewer-only and have no raster projection on the 2D map.
        } else {
            this.elog("ERROR", `Raster layer ${layer.id} without geometry data`)
        }

        this.rasterLayers.set(layer.id, layer)
        return layer
    }

    _extendBoundsFromLayer(layer) {
        if (layer.srs === "EPSG:404000") {
            return;
        }
        let lb = layer.getBounds();
        if (!lb) {
            return;
        }

        lb = L.latLngBounds(lb.getSouthWest(), lb.getNorthEast());

        //fix possible antimeridian issue
        if (typeof AMUtil !== 'undefined')
            AMUtil.fixLng(lb);

        this._extendBounds(lb)
    }
    /**
     *
     * @param {LatLngBounds}bounds
     * @returns {LatLngBounds}
     * @private
     */
    _extendBounds(bounds) {
        if (!bounds)
            return this.bounds
        if (this.bounds == null) {
            this.bounds = L.latLngBounds(bounds.getSouthWest(), bounds.getNorthEast())
        } else {
            this.bounds.extend(bounds)
        }
        return this.bounds
    }

    // set workspace bounds to all inner layers covering bounds
    layersCoveredBounds() {
        if (this._layersCoveredBounds)
            return this._layersCoveredBounds
        // do not change bounds for global layer
        if (this.global) {
            return this.bounds
        }
        for (let l of this.getLayers()) {
//            if (l instanceof VectorLayerDrawing)
//                continue
            let b = l.getBounds()
            if (!b)
                continue
            if (! this._layersCoveredBounds) {
                this._layersCoveredBounds = L.latLngBounds(b.getSouthWest(), b.getNorthEast())
            } else {
                this._layersCoveredBounds.extend(b)
            }
        }

        if (!this._layersCoveredBounds)
            return null

        if (! this.bounds) {
            this.setBounds(this._layersCoveredBounds)
        } else if (!this.bounds.contains(this._layersCoveredBounds)) {
            this.setBounds(this.bounds.extend(this._layersCoveredBounds))
        }

        return this._layersCoveredBounds
    }

    // init bounds from backend data
    initBounds(bounds) {
        if (bounds && (bounds.min.lat || bounds.min.lng || bounds.max.lat || bounds.max.lng)) {
            if (bounds.min.lat > 90 || bounds.min.lat < -90 ||
                bounds.max.lat > 90 || bounds.max.lat < -90) {
                // wrong bounds coords
                return;
            }
            this.bounds = L.latLngBounds([bounds.min.lat, bounds.min.lng], [bounds.max.lat, bounds.max.lng])
            if (typeof AMUtil !== 'undefined')
                AMUtil.fixLng(this.bounds);
        }
        // extends workspace bounds to cover all layers
        this._layersCoveredBounds = null
        this.layersCoveredBounds()
    }

    setBounds(bounds) {
        if (!bounds)
            return
        this._logdebug(`bounds has been extended from ${this.bounds ? this.bounds.toBBoxString() : 'NULL'} to ${bounds.toBBoxString()}`)
        // todo need to save new bounds
        this.bounds = L.latLngBounds(bounds.getSouthWest(), bounds.getNorthEast())
        this.fire("boundschanged")
        if (this.updatePropertiesHandler)
            this.updatePropertiesHandler(this, ["bounds"]);
    }

    getFeatureDrawType(layer) {
        let shapeType = null

        if (layer instanceof L.Polygon) {
            shapeType = "Polygon"
        } else if (layer instanceof L.Polyline) {
            shapeType = "Line"
        } else if (layer instanceof L.Marker) {
            shapeType = "Marker"
        } else if (layer instanceof L.Circle) {
            shapeType = "Circle"
        }

        return shapeType
    }

    /**
     * return layer with drawtype, see getFeatureDrawType for possible draw type
     * @param drawType
     * @returns {null|unknown}
     */
    getDrawingLayerByDrawType(drawType) {
        if (! drawType)
            return null

        for (let vectorLayer of this.vectorLayers.values()) {
            if (vectorLayer.drawTypes && vectorLayer.drawTypes.includes(drawType))
                return vectorLayer
        }
        return null
    }

    getDrawingLayerForFeature(layer) {
        return this.getDrawingLayerByDrawType(this.getFeatureDrawType(layer))
    }

    getVectorLayers() {
        let o = []
        for (let obj of this.vectorLayers.values()) {
            o.push(obj)
        }
        return o;
    }

    getRasterLayers() {
        let o = []
        for (let obj of this.rasterLayers.values()) {
            o.push(obj)
        }
        return o;
    }

    /**
     *
     * @returns {(VectorLayer[]|RasterLayer[])}
     */
    getLayers() {
        let l = this.getRasterLayers()
        l.push(...this.getVectorLayers())
        return l
    }

    getNotDrawingLayers() {
        return gcUtils.objFilter(Array.from(this.getLayers()), ["typeDrawing", "!"])
    }
    getLayersFiltered(filterExpr) {
        return gcUtils.objFilter(Array.from(this.getLayers()), filterExpr)
    }

    getLayersHidden() {
        return gcUtils.objFilter(Array.from(this.getLayers()), ["visible", "!"])
    }

    getLayer(id) {
        let l = this.rasterLayers.get(id)
        if (!l) {
            l = this.vectorLayers.get(id)
        }
        return l
    }

    getMaxZoom() {
        let maxZoom = 0
        for (let obj of this.rasterLayers.values()) {
            if (obj.geometry.maxZoom > maxZoom)
                maxZoom = obj.geometry.maxZoom
        }
        return maxZoom
    }

    getMinZoom() {
        let minZoom = 32
        for (let obj of this.rasterLayers.values()) {
            if (obj.geometry.minZoom < minZoom)
                minZoom = obj.geometry.minZoom
        }
        return minZoom !== 32 ? minZoom : 0
    }

    getBounds(onlyVisible) {
        return this.bounds
    }

    updateZoomVisibility(ev) {
        if (this.getBounds() === null ||
            this.map === null ||
            this.minZoom === 0
        )
            return

        if (ev)
            this._logdebug(`zoomVisibility is called by ${ev.type}`)

        if (this.visible) {
            if (this.map._zoom >= this.minZoom) {
                //show
                if (this.hiddenByZoomlevel || !ev) {
                    this._logdebug(`is shown at zoomlevel >= ${this.map._zoom}`)
                    this.hiddenByZoomlevel = false
                    this._layersDisplay(true)
                    this.fire("visiblechanged")
                }
                this.hiddenByZoomlevel = false
            } else {
                //hide
                if (! this.hiddenByZoomlevel || !ev) {
                    this.hiddenByZoomlevel = true
                    this._logdebug(`is hidden at zoomlevel < ${this.map._zoom}`)
                    this._layersDisplay(false)
                    this.fire("visiblechanged")
                }
                this.hiddenByZoomlevel = true
            }
        }
        this.updateMarker()
    }

    /**
     * creates a new or updates an existent workspace marker.
     * marker is shown at low zoom levels where area looks like only several pixels
     * @param updateText {string | null}
     * @param updatePosition {boolean}
     */
    updateMarker(updateText, updatePosition= false) {
        // no markers for global layer
        if (this.global)
            return

        if (this.marker == null) {
            if (! this.showMarker || this.getBounds() === null || this.map === null || !this.visible)
                return

            let markerOptions = {
                riseOnHover: true,
                autoPan: true,
                pmIgnore: true, //deny editing operations by geoman drawing
                icon: markerIcons[this.mine ? 'blue' : 'green'],
            };
            this.marker = L.marker(this._markerPosition(), markerOptions).bindTooltip(this.name, {direction: "top", permanent: true, offset: [0, -40], opacity: 0.7, className: "tooltip-label"}).openTooltip();

            this.marker.on('click', function (ev) {
                mapContext.mapViewTo(this, null, true);
            }.bind(this));
            this.marker.setTooltipContent(this.name)
        }

        if (updateText)
            this.marker.setTooltipContent(updateText)

        if (updatePosition)
            this.marker.setLatLng(this._markerPosition())

        //update visibility
        let markerMaxZoom = (this.minZoom) ? this.minZoom : this.markerDefMaxZoom;
        if (! this.visible || ! this.map) {
            this.marker.removeFrom(this.map)
            this._logdebug(`marker is hidden by workspace visibility`)
        } else if (this.marker._map == null) {
            if (this.map._zoom <= markerMaxZoom && this.visible) {
                this._logdebug(`marker is shown at zoomlevel`)
                this.marker.addTo(this.map);
            }
        } else {
            if (this.map._zoom > markerMaxZoom || ! this.visible) {
                this._logdebug(`marker is hidden at zoomlevel`)
                this.marker.removeFrom(this.map)
            }
        }
    }

    _markerPosition() {
        // default marker position is at the center of the workspace
        let pos = this.getBounds()
        // but if there are several not intersected layers, we have got marker at center of nowhere
        // so as temporary solution we just take the first raster layer
        // if there is raster layer, creates marker on the center this raster layer
        if (this.rasterLayers.size > 0) {
            let p = Array.from(this.rasterLayers.values())[0].getBounds()
            pos = L.latLngBounds(p.getSouthWest(), p.getNorthEast())
            if (typeof AMUtil !== 'undefined')
                AMUtil.fixLng(pos)
            return pos.getCenter()
        }
        return pos.getCenter()
    }

    addOnMap(map) {
        this.map = map;
        for (let layer of this.rasterLayers.values()) {
            layer.addOnMap(this.map)
        }
        for (let layer of this.vectorLayers.values()) {
            layer.addOnMap(this.map)
        }

        // if minZoom wasn't init from rasterLayer, init it from workspace bounds
        if (this.minZoom === 0 && this.bounds) {
            // pixel size at current zoom
            let s = L.bounds(
                this.map.project(this.bounds.getSouthWest()),
                this.map.project(this.bounds.getNorthEast())
            ).getSize()

            let size = Math.max(s.x, s.y)
            let scale = size / WORKSPACE_MIN_SIZE_IN_PIXELS_TO_SHOW;
            if (scale) {
                // at this zoom it is shown with WORKSPACE_MIN_SIZE_IN_PIXELS_TO_SHOW size
                this.minZoom = Math.ceil(this.map.getScaleZoom(1 / scale))
            }
        }


        this.map.on("zoomend", this.updateZoomVisibility, this);
        this.updateZoomVisibility()
        this.fire("visiblechanged")
    }

    removeFromMap() {
        if (this.map == null) {
            return
        }
        for (let layer of this.rasterLayers.values()) {
            layer.removeFromMap()
        }
        for (let layer of this.vectorLayers.values()) {
            layer.removeFromMap()
        }

        this.map.off("zoomend", this.updateZoomVisibility, this);
        this.updateZoomVisibility()

        this.map = null

        this.updateMarker()
        this.fire("visiblechanged")
    }

    /**
     * adds feature defined by leaflet layer
     * returns true on success
     * @param layer
     * @param properties
     */
    addFeature(layer, properties) {
        //get vectorLayer storing the appropriate kind of shape
        let vectorLayer = this.getDrawingLayerForFeature(layer);
        if (!vectorLayer) {
            this.elog("ERROR", `Can't determine vectorLayer to store new geometry of type ${this.getFeatureDrawType(layer)}`);
            return false;
        }

        //fix antimeridian issues (see antimeridianUtils)
        if (typeof AMUtil !== 'undefined') {
            let lngShift = AMUtil.fixLayer(layer);
            if (lngShift > 0) {
                //to move the map to the new location where layer is moved to
                let mapCenter = this.map.getCenter();
                mapCenter.lng += lngShift;
                this.map.setView(mapCenter);
            }
        }

        return vectorLayer.addFeature(layer, properties)
    }

    // all features should be the same shape type
    addFeatures(llayers) {
        if (llayers.length === 0)
            return

        //get vectorLayer storing the appropriate kind of shape
        let vectorLayer = this.getDrawingLayerForFeature(llayers[0]);
        if (!vectorLayer) {
            this.elog("ERROR", `Can't determine vectorLayer to store new geometry of type ${this.getFeatureDrawType(llayers[0])}`);
            return false;
        }

        if (llayers.length === 1) {
            vectorLayer.addFeature(llayers[0])
        } else {
            vectorLayer.addFeatures(llayers)
        }
    }

    getPreview() {
        let l = this._firstRasterLayer

        if (l == null)
            l = this._firstVectorLayer

        if (l == null) {
            l = this.getDrawingLayerByDrawType("Polygon")
        }

        if (l == null) {
            return this.preview
        }

        return l.getPreview()
    }

    moveFeaturesToWorkspace(leafletLayers, targetWorkspace) {
        let vectorLayers = new Map()

        //distribute leafletLayers by vectorLayers
        for (let i in leafletLayers) {
            let l = leafletLayers[i]
            let arr = vectorLayers.get(l._maphubVectorLayer.id)
            if (! arr) {
                arr = []
                vectorLayers.set(l._maphubVectorLayer.id, arr)
            }
            arr.push(l)
        }

        //subscribe to commit event
        for (let [vl, arr] of vectorLayers) {
            this.getLayer(vl).removeFeatures(arr, function (rLayers) {
                targetWorkspace.addFeatures(rLayers)
            })
        }
    }

    show() {
        if (this.map == null)
            return

        this.visible = true; //should do at first
        this._logdebug('is shown')
        this._layersDisplay(true)
        this.map.on("zoomend", this.updateZoomVisibility, this);
        this.updateZoomVisibility()
        this.fire("visiblechanged")
        mapContext.fire("ws:visiblechanged", {workspace: this});
    }

    hide() {
        if (this.map == null)
            return
        this.visible = false; //should do at first
        this._logdebug('is hidden')
        this._layersDisplay(false)
        this.map.off("zoomend", this.updateZoomVisibility, this);
        this.updateZoomVisibility()
        this.fire("visiblechanged")
        mapContext.fire("ws:visiblechanged", {workspace: this});
    }

    _layersDisplay(enable, byUser = false) {
        this.rasterLayers.forEach(function (layer) {
            enable?layer._show(byUser):layer._hide(byUser)
        }.bind(this))
        this.vectorLayers.forEach(function (layer) {
            enable?layer._show(byUser):layer._hide(byUser)
        }.bind(this))
    }

    updateProperties(struct) {
        let changed = false;
        for (let prop of ["name", "public", "publicDownload", "description"]) {
            if (struct[prop] !== undefined) {
                if (prop === "name" && struct[prop] === "")
                    struct[prop] = this.defaultName;
                if (this[prop] !== struct[prop]) {
                    changed = true;
                    this[prop] = struct[prop];
                }
            }
        }

        if (changed) {
            if (this.updatePropertiesHandler)
                this.updatePropertiesHandler(this);
            mapContext.fire("ws:updateProperties", {workspace: this, properties: struct});
        }

        this.updateMarker(this.name)
        return changed;
    }

    isNoName() {
        return (this.name === this.defaultName || this.name === "");
    }

    isMine() {
        return this.mine
    }

    isShared() {
        return this.shared
    }

    isWritable() {
        return this.writable
    }

    async shareLinks() {
        if (! this._shareLinks) {
            try {
                this._shareLinks = await mapApi.apiGetWorkspaceShareTokens(this.id)
                for (let i in this._shareLinks) {
                    this._shareLinks[i].url = "https://" + location.hostname + "/" + this._shareLinks[i].token;
                }
            } catch (e) {
                this.elog("ERROR", "can't get share link", e)
            }
        }
        return this._shareLinks;
    }

    _geoCodeHuman() {
        let text = ''
        if (! this.geodata) {
            return
        }
        let gd = this.geodata
        if (gd.country && gd.country !== '') {
            text += gd.country

            if (gd.province && gd.province !== '') {
                text += ', ' + gd.province
            }

            if (gd.city && gd.city !== '') {
                text += ', ' + gd.city
            }
        }
        return text
    }

    isVisible() {
        return this.visible;
    }

    isVisibleReal() {
        return this.visible && !this.hiddenByZoomlevel && this.map;
    }

    _logdebug(msg) {
        if (this.debug)
            this.elog("DEBUG", `Workspace ${this.id} ${msg}`)
    }
}

export {Workspace};
